import React, { useEffect, useState, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Icons from lucide-react
import { 
  Menu, X, ChevronRight, ArrowRight, Shield, Layers, Lock, Palette, Brain,
  MapPin, Mail, Phone, Send, Users, Building2, Globe, Sparkles, ExternalLink,
  CheckCircle, AlertCircle, Clock, Beaker, LogOut, Settings, UserPlus, Trash2,
  Eye, MoreHorizontal, BookOpen, FlaskConical, Handshake, Calendar, Tag,
  ArrowUpRight, Play, Github, Percent, MessageCircle, Award, Newspaper
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// =============================================================================
// AUTH CONTEXT
// =============================================================================

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      toast.success("Logged out successfully");
    } catch (error) {
      toast.error("Logout failed");
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// =============================================================================
// LAYOUT COMPONENTS
// =============================================================================

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "About", href: "/about" },
    { name: "Services", href: "/services" },
    { name: "Platforms", href: "/platforms" },
    { name: "Journal", href: "/journal" },
    { name: "Contact", href: "/contact" },
  ];

  return (
    <header 
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-[#FDFBF7]/95 backdrop-blur-sm shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="container-width">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="logo-link"
            className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] tracking-tight"
          >
            ARAWOL
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className="text-[#5A5A5A] hover:text-[#8C3E18] transition-colors font-medium"
              >
                {link.name}
              </Link>
            ))}
            {user ? (
              <div className="flex items-center gap-4">
                {["investor", "partner", "pilot", "admin"].includes(user.role) && (
                  <>
                    <Link
                      to="/investor"
                      data-testid="investor-link"
                      className="text-[#5A5A5A] hover:text-[#8C3E18] transition-colors font-medium"
                    >
                      Investor Portal
                    </Link>
                    <Link
                      to="/docs"
                      data-testid="docs-link"
                      className="text-[#5A5A5A] hover:text-[#8C3E18] transition-colors font-medium"
                    >
                      Docs
                    </Link>
                  </>
                )}
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    data-testid="admin-link"
                    className="text-[#5A5A5A] hover:text-[#8C3E18] transition-colors font-medium"
                  >
                    Admin
                  </Link>
                )}
                <button
                  onClick={logout}
                  data-testid="logout-btn"
                  className="flex items-center gap-2 text-[#5A5A5A] hover:text-[#8C3E18] transition-colors"
                >
                  <LogOut size={18} />
                  Logout
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                data-testid="login-link"
                className="btn-primary rounded-sm text-sm px-6 py-2"
              >
                Login
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div 
            data-testid="mobile-menu"
            className="md:hidden absolute top-20 left-0 right-0 bg-[#FDFBF7] border-t border-[#E6DCD3] py-4 px-6 shadow-lg animate-slide-down z-50"
          >
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="block py-3 text-[#5A5A5A] hover:text-[#8C3E18] transition-colors font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    className="block py-3 text-[#5A5A5A] hover:text-[#8C3E18] transition-colors font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Admin
                  </Link>
                )}
                <button
                  onClick={() => { logout(); setMobileMenuOpen(false); }}
                  className="block py-3 text-[#5A5A5A] hover:text-[#8C3E18] transition-colors font-medium w-full text-left"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="block py-3 text-[#8C3E18] font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Login
              </Link>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="main-footer" className="bg-[#1A1A1A] text-white py-16">
      <div className="container-width">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <h3 className="font-['Playfair_Display'] text-2xl font-bold mb-4">ARAWOL</h3>
            <p className="text-gray-400 max-w-md leading-relaxed">
              Authentically Australian. A cultural, creative and technology group 
              building ethical platforms and enterprises for community development.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4 text-[#E6DCD3]">Quick Links</h4>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors">About</Link></li>
              <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors">Services</Link></li>
              <li><Link to="/platforms" className="text-gray-400 hover:text-white transition-colors">Platforms</Link></li>
              <li><Link to="/journal" className="text-gray-400 hover:text-white transition-colors">Journal</Link></li>
              <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-4 text-[#E6DCD3]">Connect</h4>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-center gap-2">
                <Mail size={16} />
                <span>arawol@pm.me</span>
              </li>
              <li className="flex items-center gap-2">
                <MapPin size={16} />
                <span>Sydney, Australia</span>
              </li>
            </ul>
            <div className="mt-6 pt-6 border-t border-gray-800">
              <a 
                href="https://www.arawol.art" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#E6DCD3] hover:text-white transition-colors flex items-center gap-2"
              >
                <Palette size={16} />
                arawol.art
                <ExternalLink size={14} />
              </a>
              <p className="text-gray-500 text-sm mt-1">Our art storefront</p>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {currentYear} Arawol Pty Ltd. All rights reserved.
          </p>
          <p className="text-gray-500 text-sm">
            Built on the lands of the Gadigal people of the Eora Nation.
          </p>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-20">
        {children}
      </main>
      <Footer />
    </div>
  );
};

// =============================================================================
// HOME PAGE
// =============================================================================

const HomePage = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [posts, setPosts] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [press, setPress] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, platformsRes, journalRes, certsRes, pressRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/platforms`),
        axios.get(`${API}/journal`),
        axios.get(`${API}/certifications`),
        axios.get(`${API}/press`)
      ]);
      setServices(servicesRes.data.slice(0, 3));
      setPlatforms(platformsRes.data.slice(0, 3));
      setPosts(journalRes.data.slice(0, 2));
      setCertifications(certsRes.data);
      setPress(pressRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  };

  const iconMap = {
    Shield: Shield,
    Layers: Layers,
    Lock: Lock,
    Palette: Palette,
    Brain: Brain,
    Users: Users,
    Globe: Globe,
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="min-h-[90vh] flex items-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#FDFBF7]" />
        <div className="container-width relative z-10">
          <div className="max-w-4xl">
            <p className="text-[#8C3E18] font-medium mb-4 animate-slide-up opacity-0" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
              Authentically Australian
            </p>
            <h1 
              data-testid="hero-title"
              className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1A1A] leading-tight mb-6 animate-slide-up opacity-0"
              style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}
            >
              Building Ethical Platforms, <br />
              <span className="text-[#8C3E18]">Empowering Communities</span>
            </h1>
            <p 
              className="text-lg md:text-xl text-[#5A5A5A] max-w-2xl mb-10 leading-relaxed animate-slide-up opacity-0"
              style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}
            >
              Arawol is a cultural, creative and technology group with Aboriginal roots, 
              building ethical platforms and enterprises that serve Australian communities 
              and beyond.
            </p>
            <div 
              className="flex flex-wrap gap-4 animate-slide-up opacity-0"
              style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}
            >
              <button
                data-testid="hero-cta-services"
                onClick={() => navigate("/services")}
                className="btn-primary rounded-sm flex items-center gap-2"
              >
                Our Services
                <ArrowRight size={18} />
              </button>
              <button
                data-testid="hero-cta-platforms"
                onClick={() => navigate("/platforms")}
                className="btn-outline rounded-sm flex items-center gap-2"
              >
                Explore Platforms
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Decorative element */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1/3 h-2/3 bg-[#F5F2EB] rounded-l-full opacity-50 hidden lg:block" />
      </section>

      {/* Services Preview */}
      <section className="section-padding bg-[#F5F2EB]">
        <div className="container-width">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
            <div>
              <p className="text-[#8C3E18] font-medium mb-2">What We Do</p>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A1A1A]">
                Our Services
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="view-all-services"
              className="mt-4 md:mt-0 text-[#8C3E18] font-medium flex items-center gap-2 hover:gap-3 transition-all"
            >
              View All Services
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Shield;
              return (
                <div
                  key={service.service_id}
                  data-testid={`service-preview-${index}`}
                  className="card-elevated rounded-sm animate-slide-up opacity-0"
                  style={{ animationDelay: `${index * 100}ms`, animationFillMode: 'forwards' }}
                >
                  <div className="w-12 h-12 bg-[#8C3E18]/10 rounded-full flex items-center justify-center mb-6">
                    <IconComponent className="text-[#8C3E18]" size={24} />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold mb-3 text-[#1A1A1A]">
                    {service.title}
                  </h3>
                  <p className="text-[#5A5A5A] leading-relaxed line-clamp-3">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platforms Preview */}
      <section className="section-padding">
        <div className="container-width">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
            <div>
              <p className="text-[#8C3E18] font-medium mb-2">Our Ecosystem</p>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A1A1A]">
                Platforms We Build
              </h2>
            </div>
            <Link
              to="/platforms"
              data-testid="view-all-platforms"
              className="mt-4 md:mt-0 text-[#8C3E18] font-medium flex items-center gap-2 hover:gap-3 transition-all"
            >
              View All Platforms
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {platforms.map((platform, index) => (
              <div
                key={platform.platform_id}
                data-testid={`platform-preview-${index}`}
                className="card-elevated rounded-sm"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className={`badge-${platform.status}`}>
                    {platform.status}
                  </span>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl font-semibold mb-3 text-[#1A1A1A]">
                  {platform.name}
                </h3>
                <p className="text-[#5A5A5A] leading-relaxed line-clamp-3">
                  {platform.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Journal Preview */}
      {posts.length > 0 && (
        <section className="section-padding bg-[#F5F2EB]">
          <div className="container-width">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
              <div>
                <p className="text-[#8C3E18] font-medium mb-2">Latest Thinking</p>
                <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A1A1A]">
                  From The Journal
                </h2>
              </div>
              <Link
                to="/journal"
                data-testid="view-all-journal"
                className="mt-4 md:mt-0 text-[#8C3E18] font-medium flex items-center gap-2 hover:gap-3 transition-all"
              >
                Read All Articles
                <ArrowRight size={18} />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {posts.map((post, index) => (
                <Link
                  key={post.post_id}
                  to={`/journal/${post.slug}`}
                  data-testid={`journal-preview-${index}`}
                  className="card-elevated rounded-sm group"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-3 py-1 bg-[#8C3E18]/10 text-[#8C3E18] text-xs font-medium rounded-full capitalize">
                      {post.category}
                    </span>
                    <span className="text-[#5A5A5A] text-sm">
                      {formatDate(post.published_at)}
                    </span>
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1A1A1A] mb-3 group-hover:text-[#8C3E18] transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-[#5A5A5A] leading-relaxed line-clamp-2">
                    {post.excerpt}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Press & Media */}
      {press.length > 0 && (
        <section className="section-padding">
          <div className="container-width">
            <div className="text-center mb-12">
              <p className="text-[#8C3E18] font-medium mb-2">In The Media</p>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A1A1A]">
                Press & Coverage
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {press.map((item, index) => (
                <div 
                  key={item.press_id}
                  data-testid={`press-${index}`}
                  className="card-elevated rounded-sm flex gap-6"
                >
                  <div className="w-16 h-16 bg-[#F5F2EB] rounded-full flex items-center justify-center shrink-0">
                    <Newspaper className="text-[#8C3E18]" size={28} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-[#8C3E18] font-medium mb-1">
                      {item.publication} • {item.date}
                    </p>
                    <h3 className="font-semibold text-[#1A1A1A] mb-2">{item.title}</h3>
                    {item.excerpt && (
                      <p className="text-[#5A5A5A] text-sm line-clamp-2">{item.excerpt}</p>
                    )}
                    {item.url && (
                      <a 
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#8C3E18] text-sm font-medium mt-2 inline-flex items-center gap-1 hover:gap-2 transition-all"
                      >
                        Read More <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Certifications & Affiliations */}
      {certifications.length > 0 && (
        <section className="py-16 bg-[#F5F2EB]">
          <div className="container-width">
            <div className="text-center mb-10">
              <p className="text-[#8C3E18] font-medium mb-2">Trusted & Verified</p>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#1A1A1A]">
                Certifications & Affiliations
              </h2>
            </div>
            <div className="flex flex-wrap justify-center gap-8">
              {certifications.map((cert, index) => (
                <div 
                  key={cert.cert_id}
                  data-testid={`cert-${index}`}
                  className="flex items-center gap-4 bg-white px-6 py-4 rounded-sm shadow-sm"
                >
                  <div className="w-12 h-12 bg-[#8C3E18]/10 rounded-full flex items-center justify-center">
                    <Award className="text-[#8C3E18]" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A1A1A]">{cert.name}</p>
                    <p className="text-sm text-[#5A5A5A]">{cert.issuer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="section-padding bg-[#1A1A1A]">
        <div className="container-width text-center">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Partner With Us?
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto mb-10 text-lg">
            Whether you're seeking cultural accreditation, platform development, or strategic advisory, 
            we're here to help build something meaningful together.
          </p>
          <button
            data-testid="cta-contact"
            onClick={() => navigate("/contact")}
            className="bg-[#8C3E18] text-white px-10 py-4 rounded-sm font-medium hover:bg-[#6B2E10] transition-colors inline-flex items-center gap-2"
          >
            Start a Conversation
            <ArrowRight size={18} />
          </button>
        </div>
      </section>
    </div>
  );
};

// =============================================================================
// ABOUT PAGE
// =============================================================================

const AboutPage = () => {
  const [team, setTeam] = useState([]);

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${API}/team`);
      setTeam(response.data);
    } catch (error) {
      console.error("Failed to fetch team:", error);
    }
  };

  const values = [
    {
      title: "Cultural Sovereignty",
      description: "Indigenous communities control their own cultural narratives and digital assets."
    },
    {
      title: "Ethical Technology",
      description: "Building platforms that serve communities rather than exploit them."
    },
    {
      title: "Authentic Partnership",
      description: "Genuine collaboration based on respect, reciprocity, and shared benefit."
    },
    {
      title: "Sustainable Impact",
      description: "Creating lasting value that benefits generations to come."
    }
  ];

  const timeline = [
    { year: "Foundation", event: "Established as an Indigenous-led technology and cultural enterprise" },
    { year: "Growth", event: "Expanded services to include cultural accreditation and platform development" },
    { year: "Innovation", event: "Launched pilot programmes for digital sovereignty initiatives" },
    { year: "Future", event: "Building the next generation of ethical Indigenous platforms" }
  ];

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="section-padding">
        <div className="container-width">
          <div className="max-w-3xl">
            <p className="text-[#8C3E18] font-medium mb-4">About Arawol</p>
            <h1 
              data-testid="about-title"
              className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#1A1A1A] leading-tight mb-6"
            >
              Aboriginal Roots. <br />
              Australian Innovation.
            </h1>
            <p className="text-lg text-[#5A5A5A] leading-relaxed">
              Arawol is a cultural, creative and technology organisation with deep Aboriginal 
              roots. We build ethical platforms and systems that empower Australian communities, 
              blending traditional wisdom with modern innovation to create meaningful impact.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-[#F5F2EB]">
        <div className="container-width">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-12">
            Our Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                data-testid={`value-${index}`}
                className="flex gap-6"
              >
                <div className="w-12 h-12 bg-[#8C3E18] rounded-full flex items-center justify-center shrink-0">
                  <CheckCircle className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold mb-2 text-[#1A1A1A]">
                    {value.title}
                  </h3>
                  <p className="text-[#5A5A5A] leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-padding">
        <div className="container-width">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-12">
            Our Journey
          </h2>
          <div className="space-y-8">
            {timeline.map((item, index) => (
              <div 
                key={index}
                data-testid={`timeline-${index}`}
                className="flex gap-6 items-start"
              >
                <div className="w-24 shrink-0">
                  <span className="text-[#8C3E18] font-semibold">{item.year}</span>
                </div>
                <div className="flex-1 pb-8 border-l-2 border-[#E6DCD3] pl-6 relative">
                  <div className="absolute -left-2 top-0 w-4 h-4 bg-[#8C3E18] rounded-full" />
                  <p className="text-[#5A5A5A] leading-relaxed">{item.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-padding bg-[#F5F2EB]">
        <div className="container-width">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-12">
            Leadership Team
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div 
                key={member.member_id}
                data-testid={`team-member-${index}`}
                className="card-elevated rounded-sm text-center"
              >
                <div className="w-24 h-24 bg-[#E6DCD3] rounded-full mx-auto mb-6 flex items-center justify-center">
                  {member.photo_url ? (
                    <img 
                      src={member.photo_url} 
                      alt={member.name}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <Users className="text-[#8C3E18]" size={40} />
                  )}
                </div>
                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1A1A1A]">
                  {member.name}
                </h3>
                <p className="text-[#8C3E18] font-medium mb-4">{member.title}</p>
                <p className="text-[#5A5A5A] leading-relaxed">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// =============================================================================
// SERVICES PAGE
// =============================================================================

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error("Failed to fetch services:", error);
    }
  };

  const iconMap = {
    Shield: Shield,
    Layers: Layers,
    Lock: Lock,
    Palette: Palette,
    Brain: Brain,
    Users: Users,
  };

  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="section-padding">
        <div className="container-width">
          <div className="max-w-3xl">
            <p className="text-[#8C3E18] font-medium mb-4">Our Services</p>
            <h1 
              data-testid="services-title"
              className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#1A1A1A] leading-tight mb-6"
            >
              Expertise That Empowers
            </h1>
            <p className="text-lg text-[#5A5A5A] leading-relaxed">
              From cultural accreditation to platform development, we offer specialised services 
              that help organisations engage authentically with Indigenous communities and build 
              ethical technology solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-padding bg-[#F5F2EB]">
        <div className="container-width">
          <div className="space-y-12">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Shield;
              return (
                <div
                  key={service.service_id}
                  data-testid={`service-card-${index}`}
                  className="card-elevated rounded-sm grid grid-cols-1 lg:grid-cols-12 gap-8"
                >
                  <div className="lg:col-span-8">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-14 h-14 bg-[#8C3E18]/10 rounded-full flex items-center justify-center">
                        <IconComponent className="text-[#8C3E18]" size={28} />
                      </div>
                      <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#1A1A1A]">
                        {service.title}
                      </h2>
                    </div>
                    <p className="text-[#5A5A5A] leading-relaxed mb-6">
                      {service.description}
                    </p>
                  </div>
                  <div className="lg:col-span-4 space-y-6">
                    <div>
                      <h4 className="text-sm font-semibold text-[#8C3E18] uppercase tracking-wider mb-2">
                        Who It's For
                      </h4>
                      <p className="text-[#5A5A5A] text-sm leading-relaxed">
                        {service.who_its_for}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-[#8C3E18] uppercase tracking-wider mb-2">
                        Engagement Models
                      </h4>
                      <p className="text-[#5A5A5A] text-sm leading-relaxed">
                        {service.engagement_models}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-width text-center">
          <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A1A1A] mb-6">
            Interested in Our Services?
          </h2>
          <p className="text-[#5A5A5A] max-w-xl mx-auto mb-8">
            Let's discuss how we can help your organisation achieve its goals while 
            respecting Indigenous values and communities.
          </p>
          <button
            data-testid="services-cta"
            onClick={() => navigate("/contact")}
            className="btn-primary rounded-sm inline-flex items-center gap-2"
          >
            Start a Conversation
            <ArrowRight size={18} />
          </button>
        </div>
      </section>
    </div>
  );
};

// =============================================================================
// PLATFORMS PAGE
// =============================================================================

const PlatformsPage = () => {
  const [platforms, setPlatforms] = useState([]);

  useEffect(() => {
    fetchPlatforms();
  }, []);

  const fetchPlatforms = async () => {
    try {
      const response = await axios.get(`${API}/platforms`);
      setPlatforms(response.data);
    } catch (error) {
      console.error("Failed to fetch platforms:", error);
    }
  };

  const statusIcons = {
    live: CheckCircle,
    beta: Beaker,
    private: Lock,
    concept: Sparkles
  };

  return (
    <div data-testid="platforms-page">
      {/* Hero */}
      <section className="section-padding">
        <div className="container-width">
          <div className="max-w-3xl">
            <p className="text-[#8C3E18] font-medium mb-4">Our Platforms</p>
            <h1 
              data-testid="platforms-title"
              className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#1A1A1A] leading-tight mb-6"
            >
              The Arawol Ecosystem
            </h1>
            <p className="text-lg text-[#5A5A5A] leading-relaxed">
              We design and build ethical digital platforms that serve Australian communities. 
              From art commerce to creative collaboration tools, our platforms 
              address real community needs.
            </p>
          </div>
        </div>
      </section>

      {/* Legend */}
      <section className="pb-8">
        <div className="container-width">
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <span className="badge-live">Live</span>
              <span className="text-[#5A5A5A] text-sm">Active and available</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="badge-mvp">MVP</span>
              <span className="text-[#5A5A5A] text-sm">Ready to test</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="badge-consultation">In Consultation</span>
              <span className="text-[#5A5A5A] text-sm">Early stage discussions</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="badge-concept">Concept</span>
              <span className="text-[#5A5A5A] text-sm">In development</span>
            </div>
          </div>
        </div>
      </section>

      {/* Platforms Grid */}
      <section className="section-padding bg-[#F5F2EB]">
        <div className="container-width">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {platforms.map((platform, index) => {
              const StatusIcon = statusIcons[platform.status] || Sparkles;
              return (
                <div
                  key={platform.platform_id}
                  data-testid={`platform-card-${index}`}
                  className="card-elevated rounded-sm"
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className={`badge-${platform.status}`}>
                      {platform.status}
                    </span>
                    {platform.access_url && (
                      <a 
                        href={platform.access_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#8C3E18] hover:text-[#6B2E10] transition-colors"
                      >
                        <ExternalLink size={20} />
                      </a>
                    )}
                  </div>
                  <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#1A1A1A] mb-4">
                    {platform.name}
                  </h3>
                  <p className="text-[#5A5A5A] leading-relaxed mb-6">
                    {platform.description}
                  </p>
                  <div className="border-t border-[#E6DCD3] pt-6 space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="text-[#8C3E18]" size={16} />
                      <span className="text-[#5A5A5A]">{platform.audience}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Layers className="text-[#8C3E18]" size={16} />
                      <span className="text-[#5A5A5A]">{platform.tech_stack}</span>
                    </div>
                  </div>
                  <Link
                    to={`/platforms/${platform.platform_id}`}
                    className="mt-6 flex items-center gap-2 text-[#8C3E18] font-medium hover:gap-3 transition-all"
                  >
                    Learn More
                    <ArrowRight size={16} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

// =============================================================================
// CONTACT PAGE
// =============================================================================

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organisation: "",
    subject: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
      setFormData({ name: "", email: "", organisation: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="section-padding">
        <div className="container-width">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Info */}
            <div>
              <p className="text-[#8C3E18] font-medium mb-4">Get In Touch</p>
              <h1 
                data-testid="contact-title"
                className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#1A1A1A] leading-tight mb-6"
              >
                Let's Start a Conversation
              </h1>
              <p className="text-lg text-[#5A5A5A] leading-relaxed mb-10">
                Whether you're interested in our services, seeking partnership opportunities, 
                or have questions about our work, we'd love to hear from you.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#8C3E18]/10 rounded-full flex items-center justify-center shrink-0">
                    <Mail className="text-[#8C3E18]" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1A1A1A] mb-1">Email</h4>
                    <p className="text-[#5A5A5A]">arawol@pm.me</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#8C3E18]/10 rounded-full flex items-center justify-center shrink-0">
                    <MapPin className="text-[#8C3E18]" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1A1A1A] mb-1">Location</h4>
                    <p className="text-[#5A5A5A]">Sydney, Australia</p>
                    <p className="text-[#8C3E18] text-sm mt-1">Gadigal Country</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="card-elevated rounded-sm">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-[#2D4F44]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="text-[#2D4F44]" size={32} />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#1A1A1A] mb-4">
                    Thank You!
                  </h3>
                  <p className="text-[#5A5A5A] mb-6">
                    We've received your message and will be in touch soon.
                  </p>
                  <button
                    data-testid="send-another-btn"
                    onClick={() => setSubmitted(false)}
                    className="text-[#8C3E18] font-medium hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        data-testid="contact-name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="input-field rounded-sm"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        data-testid="contact-email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="input-field rounded-sm"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Organisation
                    </label>
                    <input
                      type="text"
                      name="organisation"
                      data-testid="contact-organisation"
                      value={formData.organisation}
                      onChange={handleChange}
                      className="input-field rounded-sm"
                      placeholder="Your organisation (optional)"
                    />
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      data-testid="contact-subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="input-field rounded-sm"
                      placeholder="How can we help?"
                    />
                  </div>
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      data-testid="contact-message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="input-field rounded-sm resize-none"
                      placeholder="Tell us more about your inquiry..."
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={submitting}
                    className="btn-primary rounded-sm w-full flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {submitting ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Message
                        <Send size={18} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// =============================================================================
// AUTH PAGES
// =============================================================================

const LoginPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      navigate(user.role === "admin" ? "/admin" : "/");
    }
  }, [user, navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="login-page" className="min-h-[80vh] flex items-center justify-center">
      <div className="container-width">
        <div className="max-w-md mx-auto card-elevated rounded-sm text-center">
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A1A1A] mb-4">
            Welcome Back
          </h1>
          <p className="text-[#5A5A5A] mb-8">
            Sign in to access your account. Note: Only invited users can sign up.
          </p>
          <button
            data-testid="google-login-btn"
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 bg-white border border-[#E6DCD3] px-6 py-4 rounded-sm hover:border-[#8C3E18] transition-colors"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
          <p className="text-[#5A5A5A] text-sm mt-6">
            Don't have an invitation? <Link to="/contact" className="text-[#8C3E18] hover:underline">Contact us</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        toast.error("Authentication failed");
        navigate("/login");
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        
        setUser(response.data);
        toast.success(`Welcome, ${response.data.name}!`);
        
        // Clear the hash
        window.history.replaceState(null, "", window.location.pathname);
        
        // Navigate based on role
        if (response.data.role === "admin") {
          navigate("/admin", { state: { user: response.data } });
        } else {
          navigate("/", { state: { user: response.data } });
        }
      } catch (error) {
        const message = error.response?.data?.detail || "Authentication failed";
        toast.error(message);
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#8C3E18] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-[#5A5A5A]">Authenticating...</p>
      </div>
    </div>
  );
};

// =============================================================================
// ADMIN PAGE
// =============================================================================

const AdminPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [users, setUsers] = useState([]);
  const [journal, setJournal] = useState([]);
  const [team, setTeam] = useState([]);
  const [stats, setStats] = useState([]);
  const [investorDocs, setInvestorDocs] = useState([]);
  const [investorUpdates, setInvestorUpdates] = useState([]);
  const [services, setServices] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [press, setPress] = useState([]);
  const [newInvite, setNewInvite] = useState({ email: "", role: "user" });
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.role === "admin") {
      fetchData();
    }
  }, [user, activeTab]);

  const fetchData = async () => {
    try {
      if (activeTab === "contacts") {
        const res = await axios.get(`${API}/admin/contacts`, { withCredentials: true });
        setContacts(res.data);
      } else if (activeTab === "invitations") {
        const res = await axios.get(`${API}/admin/invitations`, { withCredentials: true });
        setInvitations(res.data);
      } else if (activeTab === "users") {
        const res = await axios.get(`${API}/admin/users`, { withCredentials: true });
        setUsers(res.data);
      } else if (activeTab === "journal") {
        const res = await axios.get(`${API}/admin/journal`, { withCredentials: true });
        setJournal(res.data);
      } else if (activeTab === "team") {
        const res = await axios.get(`${API}/team`, { withCredentials: true });
        setTeam(res.data);
      } else if (activeTab === "stats") {
        const res = await axios.get(`${API}/stats`, { withCredentials: true });
        setStats(res.data);
      } else if (activeTab === "investor") {
        const [docsRes, updatesRes] = await Promise.all([
          axios.get(`${API}/investor/documents`, { withCredentials: true }),
          axios.get(`${API}/investor/updates`, { withCredentials: true })
        ]);
        setInvestorDocs(docsRes.data);
        setInvestorUpdates(updatesRes.data);
      } else if (activeTab === "services") {
        const res = await axios.get(`${API}/services`, { withCredentials: true });
        setServices(res.data);
      } else if (activeTab === "platforms") {
        const res = await axios.get(`${API}/platforms`, { withCredentials: true });
        setPlatforms(res.data);
      } else if (activeTab === "press") {
        const res = await axios.get(`${API}/press`, { withCredentials: true });
        setPress(res.data);
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/invitations`, newInvite, { withCredentials: true });
      toast.success("Invitation sent!");
      setNewInvite({ email: "", role: "user" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    }
  };

  const handleDelete = async (type, id) => {
    try {
      const endpoints = {
        invitation: `/admin/invitations/${id}`,
        contact: `/admin/contacts/${id}`,
        journal: `/admin/journal/${id}`,
        team: `/admin/team/${id}`,
        stat: `/admin/stats/${id}`,
        investorDoc: `/admin/investor/documents/${id}`,
        investorUpdate: `/admin/investor/updates/${id}`,
        service: `/admin/services/${id}`,
        platform: `/admin/platforms/${id}`,
        press: `/admin/press/${id}`
      };
      await axios.delete(`${API}${endpoints[type]}`, { withCredentials: true });
      toast.success("Deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await axios.patch(`${API}/admin/contacts/${id}/read`, {}, { withCredentials: true });
      fetchData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  // Journal Form
  const JournalForm = ({ item, onClose }) => {
    const [form, setForm] = useState(item || {
      title: "", slug: "", excerpt: "", content: "", author: "Mark Roper",
      category: "news", tags: [], published: false
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (item?.post_id) {
          await axios.put(`${API}/admin/journal/${item.post_id}`, form, { withCredentials: true });
        } else {
          await axios.post(`${API}/admin/journal`, form, { withCredentials: true });
        }
        toast.success(item ? "Updated!" : "Created!");
        onClose();
        fetchData();
      } catch (error) {
        toast.error("Failed to save");
      }
    };

    return (
      <form onSubmit={handleSubmit} className="card-elevated rounded-sm space-y-4">
        <h3 className="font-semibold text-[#1A1A1A]">{item ? "Edit" : "New"} Journal Post</h3>
        <input placeholder="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required className="input-field rounded-sm" />
        <input placeholder="Slug (url-friendly)" value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} required className="input-field rounded-sm" />
        <input placeholder="Excerpt (short summary)" value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} required className="input-field rounded-sm" />
        <textarea placeholder="Content (supports markdown)" value={form.content} onChange={e => setForm({...form, content: e.target.value})} required rows={8} className="input-field rounded-sm" />
        <div className="grid grid-cols-2 gap-4">
          <input placeholder="Author" value={form.author} onChange={e => setForm({...form, author: e.target.value})} className="input-field rounded-sm" />
          <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="input-field rounded-sm">
            <option value="news">News</option>
            <option value="insights">Insights</option>
            <option value="case-studies">Case Studies</option>
            <option value="updates">Updates</option>
          </select>
        </div>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.published} onChange={e => setForm({...form, published: e.target.checked})} />
          <span>Published</span>
        </label>
        <div className="flex gap-2">
          <button type="submit" className="btn-primary rounded-sm">Save</button>
          <button type="button" onClick={onClose} className="btn-outline rounded-sm">Cancel</button>
        </div>
      </form>
    );
  };

  // Team Form
  const TeamForm = ({ item, onClose }) => {
    const [form, setForm] = useState(item || { name: "", title: "", bio: "", photo_url: "", order: 0 });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (item?.member_id) {
          await axios.put(`${API}/admin/team/${item.member_id}`, form, { withCredentials: true });
        } else {
          await axios.post(`${API}/admin/team`, form, { withCredentials: true });
        }
        toast.success(item ? "Updated!" : "Created!");
        onClose();
        fetchData();
      } catch (error) {
        toast.error("Failed to save");
      }
    };

    return (
      <form onSubmit={handleSubmit} className="card-elevated rounded-sm space-y-4">
        <h3 className="font-semibold text-[#1A1A1A]">{item ? "Edit" : "New"} Team Member</h3>
        <input placeholder="Full Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="input-field rounded-sm" />
        <input placeholder="Title (e.g. Chairman, CEO)" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required className="input-field rounded-sm" />
        <textarea placeholder="Bio" value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} rows={4} className="input-field rounded-sm" />
        <input placeholder="Photo URL (optional)" value={form.photo_url || ""} onChange={e => setForm({...form, photo_url: e.target.value})} className="input-field rounded-sm" />
        <input type="number" placeholder="Display Order" value={form.order} onChange={e => setForm({...form, order: parseInt(e.target.value)})} className="input-field rounded-sm w-32" />
        <div className="flex gap-2">
          <button type="submit" className="btn-primary rounded-sm">Save</button>
          <button type="button" onClick={onClose} className="btn-outline rounded-sm">Cancel</button>
        </div>
      </form>
    );
  };

  // Stats Form
  const StatsForm = ({ item, onClose }) => {
    const [form, setForm] = useState(item || { value: "", label: "", description: "", icon: "Users", order: 0 });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (item?.stat_id) {
          await axios.put(`${API}/admin/stats/${item.stat_id}`, form, { withCredentials: true });
        } else {
          await axios.post(`${API}/admin/stats`, form, { withCredentials: true });
        }
        toast.success(item ? "Updated!" : "Created!");
        onClose();
        fetchData();
      } catch (error) {
        toast.error("Failed to save");
      }
    };

    return (
      <form onSubmit={handleSubmit} className="card-elevated rounded-sm space-y-4">
        <h3 className="font-semibold text-[#1A1A1A]">{item ? "Edit" : "New"} Impact Stat</h3>
        <div className="grid grid-cols-2 gap-4">
          <input placeholder="Value (e.g. 50+)" value={form.value} onChange={e => setForm({...form, value: e.target.value})} required className="input-field rounded-sm" />
          <input placeholder="Label (e.g. Artists Supported)" value={form.label} onChange={e => setForm({...form, label: e.target.value})} required className="input-field rounded-sm" />
        </div>
        <input placeholder="Description (optional)" value={form.description || ""} onChange={e => setForm({...form, description: e.target.value})} className="input-field rounded-sm" />
        <select value={form.icon} onChange={e => setForm({...form, icon: e.target.value})} className="input-field rounded-sm">
          <option value="Users">Users</option>
          <option value="Palette">Palette</option>
          <option value="Layers">Layers</option>
          <option value="Globe">Globe</option>
          <option value="Shield">Shield</option>
        </select>
        <div className="flex gap-2">
          <button type="submit" className="btn-primary rounded-sm">Save</button>
          <button type="button" onClick={onClose} className="btn-outline rounded-sm">Cancel</button>
        </div>
      </form>
    );
  };

  // Services Form
  const ServiceForm = ({ item, onClose }) => {
    const [form, setForm] = useState(item || { 
      title: "", description: "", who_its_for: "", engagement_models: "", icon: "Shield", order: 0 
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (item?.service_id) {
          await axios.put(`${API}/admin/services/${item.service_id}`, form, { withCredentials: true });
        } else {
          await axios.post(`${API}/admin/services`, form, { withCredentials: true });
        }
        toast.success(item ? "Updated!" : "Created!");
        onClose();
        fetchData();
      } catch (error) {
        toast.error("Failed to save");
      }
    };

    return (
      <form onSubmit={handleSubmit} className="card-elevated rounded-sm space-y-4">
        <h3 className="font-semibold text-[#1A1A1A]">{item ? "Edit" : "New"} Service</h3>
        <input placeholder="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required className="input-field rounded-sm" />
        <textarea placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} required rows={3} className="input-field rounded-sm" />
        <textarea placeholder="Who It's For" value={form.who_its_for} onChange={e => setForm({...form, who_its_for: e.target.value})} required rows={2} className="input-field rounded-sm" />
        <textarea placeholder="Engagement Models" value={form.engagement_models} onChange={e => setForm({...form, engagement_models: e.target.value})} required rows={2} className="input-field rounded-sm" />
        <div className="grid grid-cols-2 gap-4">
          <select value={form.icon} onChange={e => setForm({...form, icon: e.target.value})} className="input-field rounded-sm">
            <option value="Shield">Shield</option>
            <option value="Layers">Layers</option>
            <option value="Lock">Lock</option>
            <option value="Palette">Palette</option>
            <option value="Brain">Brain</option>
            <option value="Users">Users</option>
            <option value="Globe">Globe</option>
          </select>
          <input type="number" placeholder="Order" value={form.order} onChange={e => setForm({...form, order: parseInt(e.target.value) || 0})} className="input-field rounded-sm" />
        </div>
        <div className="flex gap-2">
          <button type="submit" className="btn-primary rounded-sm">Save</button>
          <button type="button" onClick={onClose} className="btn-outline rounded-sm">Cancel</button>
        </div>
      </form>
    );
  };

  // Platforms Form
  const PlatformForm = ({ item, onClose }) => {
    const [form, setForm] = useState(item || { 
      name: "", description: "", status: "concept", audience: "", tech_stack: "", access_url: "", order: 0 
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (item?.platform_id) {
          await axios.put(`${API}/admin/platforms/${item.platform_id}`, form, { withCredentials: true });
        } else {
          await axios.post(`${API}/admin/platforms`, form, { withCredentials: true });
        }
        toast.success(item ? "Updated!" : "Created!");
        onClose();
        fetchData();
      } catch (error) {
        toast.error("Failed to save");
      }
    };

    return (
      <form onSubmit={handleSubmit} className="card-elevated rounded-sm space-y-4">
        <h3 className="font-semibold text-[#1A1A1A]">{item ? "Edit" : "New"} Platform</h3>
        <input placeholder="Platform Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="input-field rounded-sm" />
        <textarea placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} required rows={3} className="input-field rounded-sm" />
        <div className="grid grid-cols-2 gap-4">
          <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="input-field rounded-sm">
            <option value="live">Live</option>
            <option value="beta">Beta</option>
            <option value="private">Private</option>
            <option value="concept">Concept</option>
          </select>
          <input placeholder="Access URL (optional)" value={form.access_url || ""} onChange={e => setForm({...form, access_url: e.target.value})} className="input-field rounded-sm" />
        </div>
        <input placeholder="Target Audience" value={form.audience} onChange={e => setForm({...form, audience: e.target.value})} required className="input-field rounded-sm" />
        <div className="grid grid-cols-2 gap-4">
          <input placeholder="Tech Stack" value={form.tech_stack} onChange={e => setForm({...form, tech_stack: e.target.value})} required className="input-field rounded-sm" />
          <input type="number" placeholder="Order" value={form.order} onChange={e => setForm({...form, order: parseInt(e.target.value) || 0})} className="input-field rounded-sm" />
        </div>
        <div className="flex gap-2">
          <button type="submit" className="btn-primary rounded-sm">Save</button>
          <button type="button" onClick={onClose} className="btn-outline rounded-sm">Cancel</button>
        </div>
      </form>
    );
  };

  // Press Form
  const PressForm = ({ item, onClose }) => {
    const [form, setForm] = useState(item || { 
      title: "", publication: "", date: "", url: "", logo_url: "", excerpt: "", order: 0 
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (item?.press_id) {
          await axios.put(`${API}/admin/press/${item.press_id}`, form, { withCredentials: true });
        } else {
          await axios.post(`${API}/admin/press`, form, { withCredentials: true });
        }
        toast.success(item ? "Updated!" : "Created!");
        onClose();
        fetchData();
      } catch (error) {
        toast.error("Failed to save");
      }
    };

    return (
      <form onSubmit={handleSubmit} className="card-elevated rounded-sm space-y-4">
        <h3 className="font-semibold text-[#1A1A1A]">{item ? "Edit" : "New"} Press Item</h3>
        <input placeholder="Article Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required className="input-field rounded-sm" />
        <div className="grid grid-cols-2 gap-4">
          <input placeholder="Publication Name" value={form.publication} onChange={e => setForm({...form, publication: e.target.value})} required className="input-field rounded-sm" />
          <input placeholder="Date (e.g. March 2024)" value={form.date} onChange={e => setForm({...form, date: e.target.value})} required className="input-field rounded-sm" />
        </div>
        <input placeholder="Article URL (optional)" value={form.url || ""} onChange={e => setForm({...form, url: e.target.value})} className="input-field rounded-sm" />
        <textarea placeholder="Excerpt (optional)" value={form.excerpt || ""} onChange={e => setForm({...form, excerpt: e.target.value})} rows={2} className="input-field rounded-sm" />
        <input type="number" placeholder="Order" value={form.order} onChange={e => setForm({...form, order: parseInt(e.target.value) || 0})} className="input-field rounded-sm w-32" />
        <div className="flex gap-2">
          <button type="submit" className="btn-primary rounded-sm">Save</button>
          <button type="button" onClick={onClose} className="btn-outline rounded-sm">Cancel</button>
        </div>
      </form>
    );
  };

  // Investor Doc Form
  const InvestorDocForm = ({ item, onClose }) => {
    const [form, setForm] = useState(item || { 
      title: "", description: "", category: "pitch-deck", content: "", version: "1.0", is_confidential: true, order: 0 
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (item?.doc_id) {
          await axios.put(`${API}/admin/investor/documents/${item.doc_id}`, form, { withCredentials: true });
        } else {
          await axios.post(`${API}/admin/investor/documents`, form, { withCredentials: true });
        }
        toast.success(item ? "Updated!" : "Created!");
        onClose();
        fetchData();
      } catch (error) {
        toast.error("Failed to save");
      }
    };

    return (
      <form onSubmit={handleSubmit} className="card-elevated rounded-sm space-y-4">
        <h3 className="font-semibold text-[#1A1A1A]">{item ? "Edit" : "New"} Investor Document</h3>
        <input placeholder="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required className="input-field rounded-sm" />
        <input placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} required className="input-field rounded-sm" />
        <div className="grid grid-cols-2 gap-4">
          <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="input-field rounded-sm">
            <option value="pitch-deck">Pitch Deck</option>
            <option value="financials">Financials</option>
            <option value="reports">Reports</option>
            <option value="legal">Legal</option>
          </select>
          <input placeholder="Version" value={form.version} onChange={e => setForm({...form, version: e.target.value})} className="input-field rounded-sm" />
        </div>
        <textarea placeholder="Content (supports markdown)" value={form.content || ""} onChange={e => setForm({...form, content: e.target.value})} rows={10} className="input-field rounded-sm" />
        <div className="flex gap-2">
          <button type="submit" className="btn-primary rounded-sm">Save</button>
          <button type="button" onClick={onClose} className="btn-outline rounded-sm">Cancel</button>
        </div>
      </form>
    );
  };

  // Investor Update Form
  const InvestorUpdateForm = ({ item, onClose }) => {
    const [form, setForm] = useState(item || { title: "", content: "", category: "milestone" });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await axios.post(`${API}/admin/investor/updates`, form, { withCredentials: true });
        toast.success("Created!");
        onClose();
        fetchData();
      } catch (error) {
        toast.error("Failed to save");
      }
    };

    return (
      <form onSubmit={handleSubmit} className="card-elevated rounded-sm space-y-4">
        <h3 className="font-semibold text-[#1A1A1A]">New Investor Update</h3>
        <input placeholder="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required className="input-field rounded-sm" />
        <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="input-field rounded-sm">
          <option value="milestone">Milestone</option>
          <option value="announcement">Announcement</option>
          <option value="quarterly">Quarterly Update</option>
          <option value="monthly">Monthly Update</option>
        </select>
        <textarea placeholder="Content" value={form.content} onChange={e => setForm({...form, content: e.target.value})} required rows={6} className="input-field rounded-sm" />
        <div className="flex gap-2">
          <button type="submit" className="btn-primary rounded-sm">Save</button>
          <button type="button" onClick={onClose} className="btn-outline rounded-sm">Cancel</button>
        </div>
      </form>
    );
  };

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#8C3E18] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user || user.role !== "admin") {
    return null;
  }

  const tabs = ["contacts", "invitations", "users", "services", "platforms", "press", "journal", "team", "investor"];

  return (
    <div data-testid="admin-page" className="section-padding">
      <div className="container-width">
        <div className="mb-8">
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A1A1A] mb-2">
            Admin Dashboard
          </h1>
          <p className="text-[#5A5A5A]">Manage content, users, and investor materials</p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-[#E6DCD3] pb-4">
          {tabs.map((tab) => (
            <button
              key={tab}
              data-testid={`admin-tab-${tab}`}
              onClick={() => { setActiveTab(tab); setShowForm(false); setEditingItem(null); }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors capitalize ${
                activeTab === tab 
                  ? "bg-[#8C3E18] text-white" 
                  : "bg-[#F5F2EB] text-[#5A5A5A] hover:bg-[#E6DCD3]"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Contacts Tab */}
        {activeTab === "contacts" && (
          <div className="space-y-4">
            {contacts.length === 0 ? (
              <p className="text-[#5A5A5A] text-center py-12">No contact submissions yet</p>
            ) : (
              contacts.map((contact) => (
                <div key={contact.submission_id} className={`card-elevated rounded-sm ${!contact.is_read ? 'border-l-4 border-l-[#8C3E18]' : ''}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-semibold text-[#1A1A1A]">{contact.name}</h3>
                      <p className="text-sm text-[#5A5A5A]">{contact.email}</p>
                    </div>
                    <div className="flex gap-2">
                      {!contact.is_read && (
                        <button onClick={() => handleMarkRead(contact.submission_id)} className="p-2 text-[#5A5A5A] hover:text-[#2D4F44]"><Eye size={18} /></button>
                      )}
                      <button onClick={() => handleDelete('contact', contact.submission_id)} className="p-2 text-[#5A5A5A] hover:text-red-600"><Trash2 size={18} /></button>
                    </div>
                  </div>
                  <p className="font-medium text-[#1A1A1A] mb-2">{contact.subject}</p>
                  <p className="text-[#5A5A5A]">{contact.message}</p>
                </div>
              ))
            )}
          </div>
        )}

        {/* Invitations Tab */}
        {activeTab === "invitations" && (
          <div>
            <form onSubmit={handleInvite} className="card-elevated rounded-sm mb-8">
              <h3 className="font-semibold text-[#1A1A1A] mb-4">Send New Invitation</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input type="email" value={newInvite.email} onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })} placeholder="Email address" required className="input-field rounded-sm" />
                <select value={newInvite.role} onChange={(e) => setNewInvite({ ...newInvite, role: e.target.value })} className="input-field rounded-sm">
                  <option value="user">User</option>
                  <option value="partner">Partner</option>
                  <option value="investor">Investor</option>
                  <option value="pilot">Pilot</option>
                  <option value="admin">Admin</option>
                </select>
                <button type="submit" className="btn-primary rounded-sm flex items-center justify-center gap-2"><UserPlus size={18} /> Send Invite</button>
              </div>
            </form>
            <div className="space-y-4">
              {invitations.map((inv) => (
                <div key={inv.invitation_id} className="card-elevated rounded-sm flex justify-between items-center">
                  <div>
                    <p className="font-medium text-[#1A1A1A]">{inv.email}</p>
                    <p className="text-sm text-[#5A5A5A]">Role: {inv.role} | {inv.used ? "Used" : "Pending"}</p>
                  </div>
                  {!inv.used && <button onClick={() => handleDelete('invitation', inv.invitation_id)} className="p-2 text-[#5A5A5A] hover:text-red-600"><Trash2 size={18} /></button>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="space-y-4">
            {users.map((u) => (
              <div key={u.user_id} className="card-elevated rounded-sm flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#E6DCD3] rounded-full flex items-center justify-center">
                    {u.picture ? <img src={u.picture} alt="" className="w-full h-full rounded-full object-cover" /> : <Users size={20} className="text-[#8C3E18]" />}
                  </div>
                  <div>
                    <p className="font-medium text-[#1A1A1A]">{u.name}</p>
                    <p className="text-sm text-[#5A5A5A]">{u.email}</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${u.role === "admin" ? "bg-[#8C3E18]/10 text-[#8C3E18]" : "bg-[#2D4F44]/10 text-[#2D4F44]"}`}>{u.role}</span>
              </div>
            ))}
          </div>
        )}

        {/* Services Tab */}
        {activeTab === "services" && (
          <div>
            {showForm ? (
              <ServiceForm item={editingItem} onClose={() => { setShowForm(false); setEditingItem(null); }} />
            ) : (
              <>
                <button onClick={() => setShowForm(true)} data-testid="new-service-btn" className="btn-primary rounded-sm mb-6">+ New Service</button>
                <div className="space-y-4">
                  {services.map((service) => (
                    <div key={service.service_id} data-testid={`service-item-${service.service_id}`} className="card-elevated rounded-sm flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xs text-[#8C3E18] bg-[#8C3E18]/10 px-2 py-1 rounded">{service.icon}</span>
                          <span className="text-xs text-[#5A5A5A]">Order: {service.order}</span>
                        </div>
                        <h3 className="font-semibold text-[#1A1A1A]">{service.title}</h3>
                        <p className="text-sm text-[#5A5A5A] line-clamp-2 mt-1">{service.description}</p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button onClick={() => { setEditingItem(service); setShowForm(true); }} className="p-2 text-[#5A5A5A] hover:text-[#8C3E18]"><Eye size={18} /></button>
                        <button onClick={() => handleDelete('service', service.service_id)} className="p-2 text-[#5A5A5A] hover:text-red-600"><Trash2 size={18} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Platforms Tab */}
        {activeTab === "platforms" && (
          <div>
            {showForm ? (
              <PlatformForm item={editingItem} onClose={() => { setShowForm(false); setEditingItem(null); }} />
            ) : (
              <>
                <button onClick={() => setShowForm(true)} data-testid="new-platform-btn" className="btn-primary rounded-sm mb-6">+ New Platform</button>
                <div className="space-y-4">
                  {platforms.map((platform) => (
                    <div key={platform.platform_id} data-testid={`platform-item-${platform.platform_id}`} className="card-elevated rounded-sm flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`badge-${platform.status}`}>{platform.status}</span>
                          <span className="text-xs text-[#5A5A5A]">Order: {platform.order}</span>
                        </div>
                        <h3 className="font-semibold text-[#1A1A1A]">{platform.name}</h3>
                        <p className="text-sm text-[#5A5A5A] line-clamp-2 mt-1">{platform.description}</p>
                        <p className="text-xs text-[#8C3E18] mt-2">{platform.tech_stack}</p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button onClick={() => { setEditingItem(platform); setShowForm(true); }} className="p-2 text-[#5A5A5A] hover:text-[#8C3E18]"><Eye size={18} /></button>
                        <button onClick={() => handleDelete('platform', platform.platform_id)} className="p-2 text-[#5A5A5A] hover:text-red-600"><Trash2 size={18} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Press Tab */}
        {activeTab === "press" && (
          <div>
            {showForm ? (
              <PressForm item={editingItem} onClose={() => { setShowForm(false); setEditingItem(null); }} />
            ) : (
              <>
                <button onClick={() => setShowForm(true)} data-testid="new-press-btn" className="btn-primary rounded-sm mb-6">+ New Press Item</button>
                <div className="space-y-4">
                  {press.map((item) => (
                    <div key={item.press_id} data-testid={`press-item-${item.press_id}`} className="card-elevated rounded-sm flex justify-between items-center">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xs text-[#8C3E18] font-medium">{item.publication}</span>
                          <span className="text-xs text-[#5A5A5A]">{item.date}</span>
                        </div>
                        <h3 className="font-semibold text-[#1A1A1A]">{item.title}</h3>
                        {item.excerpt && <p className="text-sm text-[#5A5A5A] line-clamp-1 mt-1">{item.excerpt}</p>}
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button onClick={() => { setEditingItem(item); setShowForm(true); }} className="p-2 text-[#5A5A5A] hover:text-[#8C3E18]"><Eye size={18} /></button>
                        <button onClick={() => handleDelete('press', item.press_id)} className="p-2 text-[#5A5A5A] hover:text-red-600"><Trash2 size={18} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Journal Tab */}
        {activeTab === "journal" && (
          <div>
            {showForm ? (
              <JournalForm item={editingItem} onClose={() => { setShowForm(false); setEditingItem(null); }} />
            ) : (
              <>
                <button onClick={() => setShowForm(true)} className="btn-primary rounded-sm mb-6">+ New Post</button>
                <div className="space-y-4">
                  {journal.map((post) => (
                    <div key={post.post_id} className="card-elevated rounded-sm flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${post.published ? 'bg-[#2D4F44]/10 text-[#2D4F44]' : 'bg-[#D4AF37]/10 text-[#8C7018]'}`}>
                            {post.published ? 'Published' : 'Draft'}
                          </span>
                          <span className="text-xs text-[#5A5A5A]">{post.category}</span>
                        </div>
                        <h3 className="font-semibold text-[#1A1A1A]">{post.title}</h3>
                        <p className="text-sm text-[#5A5A5A]">By {post.author}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => { setEditingItem(post); setShowForm(true); }} className="p-2 text-[#5A5A5A] hover:text-[#8C3E18]"><Eye size={18} /></button>
                        <button onClick={() => handleDelete('journal', post.post_id)} className="p-2 text-[#5A5A5A] hover:text-red-600"><Trash2 size={18} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Team Tab */}
        {activeTab === "team" && (
          <div>
            {showForm ? (
              <TeamForm item={editingItem} onClose={() => { setShowForm(false); setEditingItem(null); }} />
            ) : (
              <>
                <button onClick={() => setShowForm(true)} className="btn-primary rounded-sm mb-6">+ New Member</button>
                <div className="space-y-4">
                  {team.map((member) => (
                    <div key={member.member_id} className="card-elevated rounded-sm flex justify-between items-center">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-[#E6DCD3] rounded-full flex items-center justify-center">
                          {member.photo_url ? <img src={member.photo_url} alt="" className="w-full h-full rounded-full object-cover" /> : <Users size={24} className="text-[#8C3E18]" />}
                        </div>
                        <div>
                          <p className="font-semibold text-[#1A1A1A]">{member.name}</p>
                          <p className="text-[#8C3E18]">{member.title}</p>
                          <p className="text-sm text-[#5A5A5A] line-clamp-1">{member.bio || 'No bio'}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => { setEditingItem(member); setShowForm(true); }} className="p-2 text-[#5A5A5A] hover:text-[#8C3E18]"><Eye size={18} /></button>
                        <button onClick={() => handleDelete('team', member.member_id)} className="p-2 text-[#5A5A5A] hover:text-red-600"><Trash2 size={18} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === "stats" && (
          <div>
            {showForm ? (
              <StatsForm item={editingItem} onClose={() => { setShowForm(false); setEditingItem(null); }} />
            ) : (
              <>
                <button onClick={() => setShowForm(true)} className="btn-primary rounded-sm mb-6">+ New Stat</button>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {stats.map((stat) => (
                    <div key={stat.stat_id} className="card-elevated rounded-sm flex justify-between items-center">
                      <div>
                        <p className="text-2xl font-bold text-[#8C3E18]">{stat.value}</p>
                        <p className="font-medium text-[#1A1A1A]">{stat.label}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => { setEditingItem(stat); setShowForm(true); }} className="p-2 text-[#5A5A5A] hover:text-[#8C3E18]"><Eye size={18} /></button>
                        <button onClick={() => handleDelete('stat', stat.stat_id)} className="p-2 text-[#5A5A5A] hover:text-red-600"><Trash2 size={18} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Investor Tab */}
        {activeTab === "investor" && (
          <div className="space-y-8">
            {/* Documents Section */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-[#1A1A1A]">Documents</h3>
                <button onClick={() => { setEditingItem(null); setShowForm('doc'); }} className="btn-outline rounded-sm text-sm">+ New Document</button>
              </div>
              {showForm === 'doc' && <InvestorDocForm item={editingItem} onClose={() => { setShowForm(false); setEditingItem(null); }} />}
              <div className="space-y-3">
                {investorDocs.map((doc) => (
                  <div key={doc.doc_id} className="card-elevated rounded-sm flex justify-between items-center">
                    <div>
                      <span className="text-xs text-[#8C3E18] uppercase">{doc.category}</span>
                      <p className="font-medium text-[#1A1A1A]">{doc.title}</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => { setEditingItem(doc); setShowForm('doc'); }} className="p-2 text-[#5A5A5A] hover:text-[#8C3E18]"><Eye size={18} /></button>
                      <button onClick={() => handleDelete('investorDoc', doc.doc_id)} className="p-2 text-[#5A5A5A] hover:text-red-600"><Trash2 size={18} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Updates Section */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-[#1A1A1A]">Updates</h3>
                <button onClick={() => { setEditingItem(null); setShowForm('update'); }} className="btn-outline rounded-sm text-sm">+ New Update</button>
              </div>
              {showForm === 'update' && <InvestorUpdateForm onClose={() => setShowForm(false)} />}
              <div className="space-y-3">
                {investorUpdates.map((update) => (
                  <div key={update.update_id} className="card-elevated rounded-sm flex justify-between items-center">
                    <div>
                      <span className="text-xs text-[#8C3E18] uppercase">{update.category}</span>
                      <p className="font-medium text-[#1A1A1A]">{update.title}</p>
                    </div>
                    <button onClick={() => handleDelete('investorUpdate', update.update_id)} className="p-2 text-[#5A5A5A] hover:text-red-600"><Trash2 size={18} /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// JOURNAL PAGE
// =============================================================================

const JournalPage = () => {
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory]);

  const fetchPosts = async () => {
    try {
      const url = selectedCategory 
        ? `${API}/journal?category=${selectedCategory}` 
        : `${API}/journal`;
      const response = await axios.get(url);
      setPosts(response.data);
    } catch (error) {
      console.error("Failed to fetch posts:", error);
    }
  };

  const categories = [
    { value: "", label: "All Posts" },
    { value: "news", label: "News" },
    { value: "insights", label: "Insights" },
    { value: "case-studies", label: "Case Studies" },
    { value: "updates", label: "Updates" }
  ];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div data-testid="journal-page">
      {/* Hero */}
      <section className="section-padding">
        <div className="container-width">
          <div className="max-w-3xl">
            <p className="text-[#8C3E18] font-medium mb-4">Journal & Insights</p>
            <h1 
              data-testid="journal-title"
              className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#1A1A1A] leading-tight mb-6"
            >
              Thoughts, News & Stories
            </h1>
            <p className="text-lg text-[#5A5A5A] leading-relaxed">
              Explore our latest thinking on ethical technology, community development, 
              and the work we're doing across Australia.
            </p>
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="pb-8">
        <div className="container-width">
          <div className="flex flex-wrap gap-3">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                data-testid={`journal-filter-${cat.value || 'all'}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === cat.value
                    ? "bg-[#8C3E18] text-white"
                    : "bg-[#F5F2EB] text-[#5A5A5A] hover:bg-[#E6DCD3]"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="section-padding bg-[#F5F2EB]">
        <div className="container-width">
          {posts.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="mx-auto text-[#8C3E18] mb-4" size={48} />
              <p className="text-[#5A5A5A]">No posts available yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <Link
                  key={post.post_id}
                  to={`/journal/${post.slug}`}
                  data-testid={`journal-post-${index}`}
                  className="card-elevated rounded-sm group"
                >
                  {post.featured_image && (
                    <div className="aspect-video bg-[#E6DCD3] rounded-sm mb-6 overflow-hidden">
                      <img 
                        src={post.featured_image} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-3 py-1 bg-[#8C3E18]/10 text-[#8C3E18] text-xs font-medium rounded-full capitalize">
                      {post.category}
                    </span>
                    <span className="text-[#5A5A5A] text-sm">
                      {formatDate(post.published_at)}
                    </span>
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1A1A1A] mb-3 group-hover:text-[#8C3E18] transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-[#5A5A5A] leading-relaxed line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center gap-2 mt-4 text-[#8C3E18] font-medium">
                    Read More
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// =============================================================================
// JOURNAL POST DETAIL PAGE
// =============================================================================

const JournalPostPage = () => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const postSlug = location.pathname.split('/').pop();

  useEffect(() => {
    fetchPost();
  }, [postSlug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/journal/${postSlug}`);
      setPost(response.data);
    } catch (error) {
      console.error("Failed to fetch post:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#8C3E18] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="section-padding text-center">
        <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-4">
          Post Not Found
        </h2>
        <p className="text-[#5A5A5A] mb-6">The article you're looking for doesn't exist.</p>
        <button
          onClick={() => navigate('/journal')}
          className="btn-primary rounded-sm"
        >
          Back to Journal
        </button>
      </div>
    );
  }

  return (
    <div data-testid="journal-post-detail">
      <article className="section-padding">
        <div className="container-width">
          <div className="max-w-3xl mx-auto">
            {/* Back link */}
            <button
              onClick={() => navigate('/journal')}
              className="flex items-center gap-2 text-[#5A5A5A] hover:text-[#8C3E18] mb-8 transition-colors"
            >
              <ChevronRight className="rotate-180" size={16} />
              Back to Journal
            </button>

            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-[#8C3E18]/10 text-[#8C3E18] text-xs font-medium rounded-full capitalize">
                  {post.category}
                </span>
                <span className="text-[#5A5A5A] text-sm">
                  {formatDate(post.published_at)}
                </span>
              </div>
              <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#1A1A1A] leading-tight mb-4">
                {post.title}
              </h1>
              <p className="text-[#8C3E18] font-medium">By {post.author}</p>
            </div>

            {/* Featured Image */}
            {post.featured_image && (
              <div className="aspect-video bg-[#E6DCD3] rounded-sm mb-8 overflow-hidden">
                <img 
                  src={post.featured_image} 
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              {post.content.split('\n\n').map((paragraph, index) => {
                if (paragraph.startsWith('## ')) {
                  return (
                    <h2 key={index} className="font-['Playfair_Display'] text-2xl font-semibold text-[#1A1A1A] mt-8 mb-4">
                      {paragraph.replace('## ', '')}
                    </h2>
                  );
                }
                if (paragraph.startsWith('- ')) {
                  const items = paragraph.split('\n').filter(line => line.startsWith('- '));
                  return (
                    <ul key={index} className="list-disc pl-6 space-y-2 my-4">
                      {items.map((item, i) => (
                        <li key={i} className="text-[#5A5A5A]">{item.replace('- ', '')}</li>
                      ))}
                    </ul>
                  );
                }
                if (paragraph.match(/^\d+\./)) {
                  const items = paragraph.split('\n').filter(line => line.match(/^\d+\./));
                  return (
                    <ol key={index} className="list-decimal pl-6 space-y-2 my-4">
                      {items.map((item, i) => (
                        <li key={i} className="text-[#5A5A5A]">{item.replace(/^\d+\.\s*/, '')}</li>
                      ))}
                    </ol>
                  );
                }
                return (
                  <p key={index} className="text-[#5A5A5A] leading-relaxed mb-4">
                    {paragraph}
                  </p>
                );
              })}
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-8 pt-8 border-t border-[#E6DCD3]">
                {post.tags.map((tag, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-[#F5F2EB] text-[#5A5A5A] text-sm rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </article>
    </div>
  );
};

// =============================================================================
// PARTNERS PAGE
// =============================================================================

const PartnersPage = () => {
  const [partners, setPartners] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/partners`);
      setPartners(response.data);
    } catch (error) {
      console.error("Failed to fetch partners:", error);
    }
  };

  const partnershipTypes = {
    strategic: { label: "Strategic Partners", icon: Handshake },
    technology: { label: "Technology Partners", icon: Layers },
    community: { label: "Community Partners", icon: Users },
    funding: { label: "Funding Partners", icon: Building2 }
  };

  const groupedPartners = partners.reduce((acc, partner) => {
    const type = partner.partnership_type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(partner);
    return acc;
  }, {});

  return (
    <div data-testid="partners-page">
      {/* Hero */}
      <section className="section-padding">
        <div className="container-width">
          <div className="max-w-3xl">
            <p className="text-[#8C3E18] font-medium mb-4">Our Partners</p>
            <h1 
              data-testid="partners-title"
              className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#1A1A1A] leading-tight mb-6"
            >
              Building Together
            </h1>
            <p className="text-lg text-[#5A5A5A] leading-relaxed">
              We believe in the power of partnership. Our collaborators share our commitment 
              to ethical technology and community-focused development.
            </p>
          </div>
        </div>
      </section>

      {/* Partners by Type */}
      <section className="section-padding bg-[#F5F2EB]">
        <div className="container-width">
          {Object.keys(groupedPartners).length === 0 ? (
            <div className="text-center py-16">
              <Handshake className="mx-auto text-[#8C3E18] mb-4" size={48} />
              <p className="text-[#5A5A5A] mb-6">Partner information coming soon.</p>
              <button
                onClick={() => navigate('/contact')}
                className="btn-primary rounded-sm"
              >
                Become a Partner
              </button>
            </div>
          ) : (
            <div className="space-y-16">
              {Object.entries(groupedPartners).map(([type, typePartners]) => {
                const typeInfo = partnershipTypes[type] || { label: type, icon: Building2 };
                const TypeIcon = typeInfo.icon;
                return (
                  <div key={type}>
                    <div className="flex items-center gap-3 mb-8">
                      <div className="w-10 h-10 bg-[#8C3E18]/10 rounded-full flex items-center justify-center">
                        <TypeIcon className="text-[#8C3E18]" size={20} />
                      </div>
                      <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#1A1A1A]">
                        {typeInfo.label}
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {typePartners.map((partner, index) => (
                        <div
                          key={partner.partner_id}
                          data-testid={`partner-${index}`}
                          className="card-elevated rounded-sm"
                        >
                          {partner.logo_url ? (
                            <div className="h-16 mb-4 flex items-center">
                              <img 
                                src={partner.logo_url} 
                                alt={partner.name}
                                className="max-h-full max-w-full object-contain"
                              />
                            </div>
                          ) : (
                            <div className="w-16 h-16 bg-[#E6DCD3] rounded-full flex items-center justify-center mb-4">
                              <Building2 className="text-[#8C3E18]" size={28} />
                            </div>
                          )}
                          <h3 className="font-semibold text-[#1A1A1A] mb-2">{partner.name}</h3>
                          <p className="text-[#5A5A5A] text-sm leading-relaxed mb-4">
                            {partner.description}
                          </p>
                          {partner.website && (
                            <a 
                              href={partner.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#8C3E18] text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all"
                            >
                              Visit Website
                              <ExternalLink size={14} />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-width text-center">
          <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A1A1A] mb-6">
            Interested in Partnering?
          </h2>
          <p className="text-[#5A5A5A] max-w-xl mx-auto mb-8">
            We're always looking for aligned organisations to collaborate with. 
            Let's explore how we can work together.
          </p>
          <button
            data-testid="partners-cta"
            onClick={() => navigate("/contact")}
            className="btn-primary rounded-sm inline-flex items-center gap-2"
          >
            Get in Touch
            <ArrowRight size={18} />
          </button>
        </div>
      </section>
    </div>
  );
};

// =============================================================================
// PLATFORM DETAIL PAGE
// =============================================================================

const PlatformDetailPage = () => {
  const [platform, setPlatform] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const platformId = location.pathname.split('/').pop();

  useEffect(() => {
    fetchPlatform();
  }, [platformId]);

  const fetchPlatform = async () => {
    try {
      const response = await axios.get(`${API}/platforms/${platformId}`);
      setPlatform(response.data);
    } catch (error) {
      console.error("Failed to fetch platform:", error);
    } finally {
      setLoading(false);
    }
  };

  const statusInfo = {
    mvp: { label: "MVP - Ready to Test", color: "badge-mvp" },
    consultation: { label: "In Consultation", color: "badge-consultation" },
    concept: { label: "Concept", color: "badge-concept" },
    live: { label: "Live", color: "badge-live" },
    beta: { label: "Beta", color: "badge-beta" }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#8C3E18] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!platform) {
    return (
      <div className="section-padding text-center">
        <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-4">
          Platform Not Found
        </h2>
        <button
          onClick={() => navigate('/platforms')}
          className="btn-primary rounded-sm"
        >
          Back to Platforms
        </button>
      </div>
    );
  }

  const info = statusInfo[platform.status] || { label: platform.status, color: "badge-concept" };

  return (
    <div data-testid="platform-detail">
      <section className="section-padding">
        <div className="container-width">
          {/* Back link */}
          <button
            onClick={() => navigate('/platforms')}
            className="flex items-center gap-2 text-[#5A5A5A] hover:text-[#8C3E18] mb-8 transition-colors"
          >
            <ChevronRight className="rotate-180" size={16} />
            Back to Platforms
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <span className={info.color}>{info.label}</span>
              <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#1A1A1A] leading-tight mt-4 mb-6">
                {platform.name}
              </h1>
              <p className="text-lg text-[#5A5A5A] leading-relaxed mb-8">
                {platform.description}
              </p>

              {platform.access_url && (
                <a
                  href={platform.access_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary rounded-sm inline-flex items-center gap-2"
                >
                  Access Platform
                  <ExternalLink size={18} />
                </a>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="card-elevated rounded-sm">
                <h3 className="font-semibold text-[#1A1A1A] mb-4">Details</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-[#8C3E18] uppercase tracking-wider mb-1">Audience</p>
                    <p className="text-[#5A5A5A]">{platform.audience}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#8C3E18] uppercase tracking-wider mb-1">Tech Stack</p>
                    <p className="text-[#5A5A5A]">{platform.tech_stack}</p>
                  </div>
                </div>
              </div>

              <div className="card-elevated rounded-sm">
                <h3 className="font-semibold text-[#1A1A1A] mb-4">Interested?</h3>
                <p className="text-[#5A5A5A] text-sm mb-4">
                  Want to learn more or become an early tester?
                </p>
                <Link
                  to="/contact"
                  className="btn-outline rounded-sm w-full flex items-center justify-center gap-2"
                >
                  Contact Us
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// =============================================================================
// INVESTOR PORTAL PAGE (GATED)
// =============================================================================

const InvestorPortalPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate("/login");
      } else if (!["investor", "partner", "pilot", "admin"].includes(user.role)) {
        setAccessDenied(true);
        setLoading(false);
      } else {
        fetchData();
      }
    }
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [docsRes, updatesRes] = await Promise.all([
        axios.get(`${API}/investor/documents`, { withCredentials: true }),
        axios.get(`${API}/investor/updates`, { withCredentials: true })
      ]);
      setDocuments(docsRes.data);
      setUpdates(updatesRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
      }
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const categories = {
    "pitch-deck": { label: "Pitch Deck", icon: Layers },
    "financials": { label: "Financials", icon: Building2 },
    "reports": { label: "Reports", icon: BookOpen },
    "updates": { label: "Updates", icon: Clock },
    "legal": { label: "Legal", icon: Shield }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#8C3E18] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="section-padding text-center">
        <div className="container-width max-w-lg mx-auto">
          <Lock className="mx-auto text-[#8C3E18] mb-6" size={64} />
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A1A1A] mb-4">
            Access Restricted
          </h1>
          <p className="text-[#5A5A5A] mb-8">
            This portal is available to investors and partners only. 
            If you believe you should have access, please contact us.
          </p>
          <button
            onClick={() => navigate("/contact")}
            className="btn-primary rounded-sm"
          >
            Request Access
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="investor-portal">
      {/* Hero */}
      <section className="section-padding bg-[#1A1A1A]">
        <div className="container-width">
          <div className="flex items-center gap-3 text-[#8C3E18] mb-4">
            <Lock size={20} />
            <span className="font-medium">Confidential</span>
          </div>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-white leading-tight mb-4">
            Investor Portal
          </h1>
          <p className="text-gray-400 max-w-2xl">
            Welcome, {user?.name}. Access confidential documents, updates, and strategic information about Arawol.
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b border-[#E6DCD3] bg-white sticky top-20 z-30">
        <div className="container-width">
          <div className="flex gap-8">
            {["overview", "documents", "updates"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 font-medium capitalize transition-colors border-b-2 ${
                  activeTab === tab
                    ? "text-[#8C3E18] border-[#8C3E18]"
                    : "text-[#5A5A5A] border-transparent hover:text-[#1A1A1A]"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-width">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-6">
                    Latest Updates
                  </h2>
                  {updates.slice(0, 3).map((update) => (
                    <div key={update.update_id} className="card-elevated rounded-sm mb-4">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="px-3 py-1 bg-[#8C3E18]/10 text-[#8C3E18] text-xs font-medium rounded-full capitalize">
                          {update.category}
                        </span>
                        <span className="text-sm text-[#5A5A5A]">{formatDate(update.created_at)}</span>
                      </div>
                      <h3 className="font-semibold text-[#1A1A1A] mb-2">{update.title}</h3>
                      <p className="text-[#5A5A5A] text-sm line-clamp-3">{update.content}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                <div className="card-elevated rounded-sm">
                  <h3 className="font-semibold text-[#1A1A1A] mb-4">Quick Access</h3>
                  <div className="space-y-3">
                    {documents.slice(0, 4).map((doc) => (
                      <button
                        key={doc.doc_id}
                        onClick={() => { setSelectedDoc(doc); setActiveTab("documents"); }}
                        className="w-full text-left flex items-center gap-3 p-3 bg-[#F5F2EB] rounded-sm hover:bg-[#E6DCD3] transition-colors"
                      >
                        <Layers className="text-[#8C3E18] shrink-0" size={18} />
                        <span className="text-sm text-[#1A1A1A] truncate">{doc.title}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="card-elevated rounded-sm">
                  <h3 className="font-semibold text-[#1A1A1A] mb-4">Need Help?</h3>
                  <p className="text-[#5A5A5A] text-sm mb-4">
                    Contact our team for questions or additional information.
                  </p>
                  <Link to="/contact" className="text-[#8C3E18] font-medium text-sm flex items-center gap-2">
                    Get in Touch <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === "documents" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="space-y-4">
                <h3 className="font-semibold text-[#1A1A1A]">Categories</h3>
                {Object.entries(categories).map(([key, { label, icon: Icon }]) => {
                  const count = documents.filter(d => d.category === key).length;
                  if (count === 0) return null;
                  return (
                    <button
                      key={key}
                      onClick={() => setSelectedDoc(documents.find(d => d.category === key))}
                      className="w-full flex items-center justify-between p-3 bg-[#F5F2EB] rounded-sm hover:bg-[#E6DCD3] transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="text-[#8C3E18]" size={18} />
                        <span className="text-[#1A1A1A]">{label}</span>
                      </div>
                      <span className="text-sm text-[#5A5A5A]">{count}</span>
                    </button>
                  );
                })}
              </div>

              <div className="lg:col-span-2">
                {selectedDoc ? (
                  <div className="card-elevated rounded-sm">
                    <div className="flex items-center justify-between mb-6">
                      <span className="px-3 py-1 bg-[#8C3E18]/10 text-[#8C3E18] text-xs font-medium rounded-full capitalize">
                        {selectedDoc.category.replace('-', ' ')}
                      </span>
                      <span className="text-sm text-[#5A5A5A]">v{selectedDoc.version}</span>
                    </div>
                    <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-4">
                      {selectedDoc.title}
                    </h2>
                    <p className="text-[#5A5A5A] mb-6">{selectedDoc.description}</p>
                    {selectedDoc.content && (
                      <div className="prose prose-sm max-w-none border-t border-[#E6DCD3] pt-6">
                        {selectedDoc.content.split('\n\n').map((para, i) => {
                          if (para.startsWith('# ')) {
                            return <h1 key={i} className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mt-6 mb-4">{para.replace('# ', '')}</h1>;
                          }
                          if (para.startsWith('## ')) {
                            return <h2 key={i} className="font-['Playfair_Display'] text-xl font-semibold text-[#1A1A1A] mt-6 mb-3">{para.replace('## ', '')}</h2>;
                          }
                          if (para.startsWith('### ')) {
                            return <h3 key={i} className="font-semibold text-[#1A1A1A] mt-4 mb-2">{para.replace('### ', '')}</h3>;
                          }
                          if (para.startsWith('- ')) {
                            return (
                              <ul key={i} className="list-disc pl-6 space-y-1 my-3">
                                {para.split('\n').filter(l => l.startsWith('- ')).map((item, j) => (
                                  <li key={j} className="text-[#5A5A5A]">{item.replace('- ', '')}</li>
                                ))}
                              </ul>
                            );
                          }
                          if (para.match(/^\d+\./)) {
                            return (
                              <ol key={i} className="list-decimal pl-6 space-y-1 my-3">
                                {para.split('\n').filter(l => l.match(/^\d+\./)).map((item, j) => (
                                  <li key={j} className="text-[#5A5A5A]">{item.replace(/^\d+\.\s*/, '')}</li>
                                ))}
                              </ol>
                            );
                          }
                          return <p key={i} className="text-[#5A5A5A] mb-3">{para}</p>;
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <BookOpen className="mx-auto text-[#8C3E18] mb-4" size={48} />
                    <p className="text-[#5A5A5A]">Select a category to view documents</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Updates Tab */}
          {activeTab === "updates" && (
            <div className="max-w-3xl">
              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-8">
                All Updates
              </h2>
              <div className="space-y-6">
                {updates.map((update) => (
                  <div key={update.update_id} className="card-elevated rounded-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="px-3 py-1 bg-[#8C3E18]/10 text-[#8C3E18] text-xs font-medium rounded-full capitalize">
                        {update.category}
                      </span>
                      <span className="text-sm text-[#5A5A5A]">{formatDate(update.created_at)}</span>
                    </div>
                    <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1A1A1A] mb-3">
                      {update.title}
                    </h3>
                    <p className="text-[#5A5A5A] whitespace-pre-line">{update.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// =============================================================================
// DOCUMENTATION HUB PAGE (GATED)
// =============================================================================

const DocsHubPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate("/login");
      } else if (!["investor", "partner", "pilot", "admin"].includes(user.role)) {
        setAccessDenied(true);
        setLoading(false);
      } else {
        fetchData();
      }
    }
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [catsRes, articlesRes] = await Promise.all([
        axios.get(`${API}/docs/categories`, { withCredentials: true }),
        axios.get(`${API}/docs/articles`, { withCredentials: true })
      ]);
      setCategories(catsRes.data);
      setArticles(articlesRes.data);
      if (catsRes.data.length > 0) {
        setSelectedCategory(catsRes.data[0]);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
      }
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const iconMap = {
    Shield: Shield,
    Layers: Layers,
    FileText: BookOpen,
    Code: Beaker,
    Heart: CheckCircle
  };

  const filteredArticles = articles.filter(a => {
    const matchesCategory = !selectedCategory || a.category_id === selectedCategory.category_id;
    const matchesSearch = !searchQuery || 
      a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.content.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#8C3E18] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="section-padding text-center">
        <div className="container-width max-w-lg mx-auto">
          <Lock className="mx-auto text-[#8C3E18] mb-6" size={64} />
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A1A1A] mb-4">
            Access Restricted
          </h1>
          <p className="text-[#5A5A5A] mb-8">
            Documentation is available to authorised partners only.
            If you believe you should have access, please contact us.
          </p>
          <button
            onClick={() => navigate("/contact")}
            className="btn-primary rounded-sm"
          >
            Request Access
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="docs-hub">
      {/* Hero */}
      <section className="py-12 bg-[#F5F2EB]">
        <div className="container-width">
          <div className="flex items-center gap-3 text-[#8C3E18] mb-4">
            <Lock size={20} />
            <span className="font-medium">Partner Access</span>
          </div>
          <h1 className="font-['Playfair_Display'] text-4xl font-bold text-[#1A1A1A] mb-4">
            Documentation Hub
          </h1>
          <p className="text-[#5A5A5A] max-w-2xl mb-6">
            Comprehensive documentation including governance frameworks, platform guides, policies, and cultural protocols.
          </p>
          {/* Search */}
          <div className="max-w-md">
            <input
              type="text"
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field rounded-sm"
            />
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container-width">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="space-y-2">
              <h3 className="font-semibold text-[#1A1A1A] mb-4">Categories</h3>
              {categories.map((cat) => {
                const Icon = iconMap[cat.icon] || BookOpen;
                const count = articles.filter(a => a.category_id === cat.category_id).length;
                return (
                  <button
                    key={cat.category_id}
                    onClick={() => { setSelectedCategory(cat); setSelectedArticle(null); }}
                    className={`w-full flex items-center justify-between p-3 rounded-sm transition-colors ${
                      selectedCategory?.category_id === cat.category_id
                        ? "bg-[#8C3E18] text-white"
                        : "bg-[#F5F2EB] text-[#1A1A1A] hover:bg-[#E6DCD3]"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon size={18} />
                      <span className="text-sm">{cat.name}</span>
                    </div>
                    <span className="text-xs opacity-70">{count}</span>
                  </button>
                );
              })}
            </div>

            {/* Content */}
            <div className="lg:col-span-3">
              {selectedArticle ? (
                <div>
                  <button
                    onClick={() => setSelectedArticle(null)}
                    className="flex items-center gap-2 text-[#5A5A5A] hover:text-[#8C3E18] mb-6 transition-colors"
                  >
                    <ChevronRight className="rotate-180" size={16} />
                    Back to articles
                  </button>
                  <div className="card-elevated rounded-sm">
                    <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A1A1A] mb-6">
                      {selectedArticle.title}
                    </h1>
                    {selectedArticle.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-6">
                        {selectedArticle.tags.map((tag, i) => (
                          <span key={i} className="px-3 py-1 bg-[#F5F2EB] text-[#5A5A5A] text-xs rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="prose max-w-none">
                      {selectedArticle.content.split('\n\n').map((para, i) => {
                        if (para.startsWith('# ')) {
                          return <h1 key={i} className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mt-8 mb-4">{para.replace('# ', '')}</h1>;
                        }
                        if (para.startsWith('## ')) {
                          return <h2 key={i} className="font-['Playfair_Display'] text-xl font-semibold text-[#1A1A1A] mt-6 mb-3">{para.replace('## ', '')}</h2>;
                        }
                        if (para.startsWith('### ')) {
                          return <h3 key={i} className="font-semibold text-[#1A1A1A] mt-4 mb-2">{para.replace('### ', '')}</h3>;
                        }
                        if (para.startsWith('- ')) {
                          return (
                            <ul key={i} className="list-disc pl-6 space-y-2 my-4">
                              {para.split('\n').filter(l => l.startsWith('- ')).map((item, j) => (
                                <li key={j} className="text-[#5A5A5A]">{item.replace('- ', '')}</li>
                              ))}
                            </ul>
                          );
                        }
                        if (para.match(/^\d+\./)) {
                          return (
                            <ol key={i} className="list-decimal pl-6 space-y-2 my-4">
                              {para.split('\n').filter(l => l.match(/^\d+\./)).map((item, j) => (
                                <li key={j} className="text-[#5A5A5A]">{item.replace(/^\d+\.\s*/, '')}</li>
                              ))}
                            </ol>
                          );
                        }
                        return <p key={i} className="text-[#5A5A5A] mb-4 leading-relaxed">{para}</p>;
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  {selectedCategory && (
                    <div className="mb-8">
                      <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-2">
                        {selectedCategory.name}
                      </h2>
                      <p className="text-[#5A5A5A]">{selectedCategory.description}</p>
                    </div>
                  )}
                  {filteredArticles.length === 0 ? (
                    <div className="text-center py-12">
                      <BookOpen className="mx-auto text-[#8C3E18] mb-4" size={48} />
                      <p className="text-[#5A5A5A]">No articles found</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {filteredArticles.map((article) => (
                        <button
                          key={article.article_id}
                          onClick={() => setSelectedArticle(article)}
                          className="card-elevated rounded-sm text-left hover:border-[#8C3E18]/30 transition-colors"
                        >
                          <h3 className="font-semibold text-[#1A1A1A] mb-2">{article.title}</h3>
                          <p className="text-[#5A5A5A] text-sm line-clamp-2">
                            {article.content.substring(0, 150).replace(/[#*]/g, '')}...
                          </p>
                          <div className="flex items-center gap-2 mt-4 text-[#8C3E18] text-sm font-medium">
                            Read Article <ArrowRight size={14} />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// =============================================================================
// APP ROUTER
// =============================================================================

const AppRouter = () => {
  const location = useLocation();
  
  // Check for session_id in URL hash synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Layout><HomePage /></Layout>} />
      <Route path="/about" element={<Layout><AboutPage /></Layout>} />
      <Route path="/services" element={<Layout><ServicesPage /></Layout>} />
      <Route path="/platforms" element={<Layout><PlatformsPage /></Layout>} />
      <Route path="/platforms/:platformId" element={<Layout><PlatformDetailPage /></Layout>} />
      <Route path="/journal" element={<Layout><JournalPage /></Layout>} />
      <Route path="/journal/:slug" element={<Layout><JournalPostPage /></Layout>} />
      <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
      <Route path="/login" element={<Layout><LoginPage /></Layout>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/admin" element={<Layout><AdminPage /></Layout>} />
      <Route path="/investor" element={<Layout><InvestorPortalPage /></Layout>} />
      <Route path="/docs" element={<Layout><DocsHubPage /></Layout>} />
    </Routes>
  );
};

// =============================================================================
// FLOATING CTA BUTTON
// =============================================================================

const FloatingCTA = () => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!isVisible) return null;

  return (
    <button
      data-testid="floating-cta"
      onClick={() => navigate("/contact")}
      className="fixed bottom-24 right-6 bg-[#8C3E18] text-white px-6 py-3 rounded-full shadow-lg hover:bg-[#6B2E10] transition-all duration-300 flex items-center gap-2 z-40 animate-fade-in"
    >
      <MessageCircle size={20} />
      <span className="font-medium">Let's Talk</span>
    </button>
  );
};

// =============================================================================
// EXIT INTENT POPUP
// =============================================================================

const ExitIntentPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const hasShown = useRef(false);

  useEffect(() => {
    // Check if already dismissed in this session
    if (sessionStorage.getItem("exitPopupDismissed")) return;

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !hasShown.current) {
        hasShown.current = true;
        setShowPopup(true);
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email, source: "exit_popup" });
      setSubmitted(true);
      toast.success("Thanks for subscribing!");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowPopup(false);
    sessionStorage.setItem("exitPopupDismissed", "true");
  };

  if (!showPopup) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in">
      <div className="bg-white rounded-sm p-8 max-w-md mx-4 relative animate-slide-up">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-[#5A5A5A] hover:text-[#1A1A1A]"
        >
          <X size={24} />
        </button>

        {submitted ? (
          <div className="text-center py-4">
            <CheckCircle className="mx-auto text-[#2D4F44] mb-4" size={48} />
            <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-2">
              You're In!
            </h3>
            <p className="text-[#5A5A5A]">
              We'll keep you updated on our latest projects and insights.
            </p>
            <button
              onClick={handleClose}
              className="mt-6 text-[#8C3E18] font-medium hover:underline"
            >
              Continue browsing
            </button>
          </div>
        ) : (
          <>
            <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-2">
              Before You Go...
            </h3>
            <p className="text-[#5A5A5A] mb-6">
              Stay updated on our platforms, projects, and insights. 
              No spam, just meaningful updates.
            </p>
            <form onSubmit={handleSubmit}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="input-field rounded-sm mb-4"
                data-testid="exit-popup-email"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="exit-popup-submit"
                className="btn-primary rounded-sm w-full disabled:opacity-50"
              >
                {submitting ? "Subscribing..." : "Keep Me Updated"}
              </button>
            </form>
            <p className="text-xs text-[#5A5A5A] mt-4 text-center">
              We respect your privacy. Unsubscribe anytime.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// LIVE CHAT WIDGET
// =============================================================================

const LiveChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/chat`, formData);
      setSubmitted(true);
      toast.success("Message sent! We'll get back to you soon.");
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({ name: "", email: "", message: "" });
    setSubmitted(false);
  };

  return (
    <>
      {/* Chat Button */}
      <button
        data-testid="chat-widget-btn"
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 bg-[#2D4F44] text-white p-4 rounded-full shadow-lg hover:bg-[#1A332B] transition-all duration-300 z-50"
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          data-testid="chat-widget-window"
          className="fixed bottom-24 right-6 w-80 bg-white rounded-sm shadow-2xl z-50 animate-slide-up overflow-hidden"
        >
          {/* Header */}
          <div className="bg-[#2D4F44] text-white p-4">
            <h4 className="font-semibold">Chat with us</h4>
            <p className="text-sm text-white/80">We typically reply within hours</p>
          </div>

          {/* Body */}
          <div className="p-4">
            {submitted ? (
              <div className="text-center py-4">
                <CheckCircle className="mx-auto text-[#2D4F44] mb-3" size={40} />
                <p className="text-[#1A1A1A] font-medium mb-1">Message Sent!</p>
                <p className="text-sm text-[#5A5A5A] mb-4">We'll be in touch soon.</p>
                <button
                  onClick={handleReset}
                  className="text-[#8C3E18] text-sm font-medium hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="input-field rounded-sm text-sm py-2"
                  data-testid="chat-name"
                />
                <input
                  type="email"
                  placeholder="Your email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="input-field rounded-sm text-sm py-2"
                  data-testid="chat-email"
                />
                <textarea
                  placeholder="How can we help?"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={3}
                  className="input-field rounded-sm text-sm py-2 resize-none"
                  data-testid="chat-message"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="chat-submit"
                  className="btn-primary rounded-sm w-full text-sm py-2 disabled:opacity-50"
                >
                  {submitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// =============================================================================
// MAIN APP
// =============================================================================

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppRouter />
        <FloatingCTA />
        <ExitIntentPopup />
        <LiveChatWidget />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
